import { useCallback, useEffect, useRef, useState } from 'react';
import Viewer from 'react-viewer';

import Stack from '@mui/material/Stack';
import { makeStyles } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import type { ToolbarConfig } from 'react-viewer/lib/ViewerProps';

// Mirrors react-viewer's ActionType enum (lib/Icon.d.ts) — the package ships
// only type declarations under lib/, so the enum can't be imported at runtime.
const VIEWER_ACTIONS = {
  reset: 7,
  zoomIn: 1,
  zoomOut: 2,
  rotateLeft: 5,
  rotateRight: 6,
  flipHorizontal: 9,
  flipVertical: 10,
};

interface ImageViewerProps {
  url: string;
  onDownload?: () => void;
}

export default function ImageViewer({ url, onDownload }: ImageViewerProps) {
  const classes = useStyles();

  const [isShow, setIsShow] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (url && viewerRef.current && !isShow) {
      setIsShow(true);
    }
  }, [url]);

  // Replaces react-viewer's toolbar (dark circles + icon font) with the same
  // MUI buttons PdfViewer uses, in the same order. Each item keeps the
  // library's actionType so the zoom/rotate/reset behavior stays untouched —
  // react-viewer handles the click via the surrounding toolbar item.
  const customToolbar = useCallback(
    (): ToolbarConfig[] => [
      {
        key: 'rotateLeft',
        actionType: VIEWER_ACTIONS.rotateLeft,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <RotateLeftOutlinedIcon />
          </IconButton>
        ),
      },
      {
        key: 'rotateRight',
        actionType: VIEWER_ACTIONS.rotateRight,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <RotateRightOutlinedIcon />
          </IconButton>
        ),
      },
      {
        key: 'zoomOut',
        actionType: VIEWER_ACTIONS.zoomOut,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <ZoomOutOutlinedIcon />
          </IconButton>
        ),
      },
      {
        key: 'zoomIn',
        actionType: VIEWER_ACTIONS.zoomIn,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <ZoomInOutlinedIcon />
          </IconButton>
        ),
      },
      {
        key: 'scaleX',
        actionType: VIEWER_ACTIONS.flipHorizontal,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <FlipOutlinedIcon />
          </IconButton>
        ),
      },
      {
        key: 'scaleY',
        actionType: VIEWER_ACTIONS.flipVertical,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <FlipOutlinedIcon className={classes.flipVerticalIcon} />
          </IconButton>
        ),
      },
      {
        key: 'reset',
        actionType: VIEWER_ACTIONS.reset,
        render: (
          <IconButton color="secondary" className={classes.toolbarButton}>
            <RestartAltOutlinedIcon />
          </IconButton>
        ),
      },
      ...(onDownload
        ? [
            {
              // Not 'download': react-viewer strips that reserved key from the
              // toolbar (after customToolbar runs) unless `downloadable` is set.
              key: 'downloadFile',
              render: (
                <IconButton
                  color="secondary"
                  onClick={onDownload}
                  className={classes.toolbarButton}
                >
                  <FileDownloadOutlinedIcon />
                </IconButton>
              ),
            },
          ]
        : []),
    ],
    [onDownload],
  );

  return (
    <Stack
      height="100%"
      width="100%"
      justifyContent={'center'}
      alignItems={'center'}
      ref={viewerRef}
      className={classes.wrapper}
    >
      <Viewer
        visible={isShow}
        container={viewerRef?.current ?? undefined}
        images={[{ src: url }]}
        showTotal={false}
        noClose={true}
        noNavbar={true}
        noImgDetails={true}
        customToolbar={customToolbar}
      />
    </Stack>
  );
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    '& .react-viewer-inline': {
      minHeight: 'calc(100vh - 170px)',
    },
    // Match PdfViewer's toolbar strip: same gradient background, and neutral
    // list items so the MUI IconButtons render like PdfViewer's instead of
    // the library's 28px dark circles.
    '& .react-viewer-footer': {
      background:
        'linear-gradient(0deg, #212121 -15%, rgba(163, 163, 163, 0) 100%)',
    },
    '& .react-viewer-toolbar': {
      height: 'auto',
      overflow: 'visible',
    },
    '& .react-viewer-toolbar li': {
      width: 'auto',
      height: 'auto',
      margin: 0,
      lineHeight: 'normal',
      borderRadius: 0,
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  // Kept identical to PdfViewer's toolbarButton so both viewers stay in sync.
  toolbarButton: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    margin: theme.spacing(0, 0.5),

    '& svg': {
      color: 'white',
    },
  },
  // MUI only ships a horizontal flip icon — rotate it for the vertical one.
  flipVerticalIcon: {
    transform: 'rotate(90deg)',
  },
}));
